// @greenlight-spec: 16-api-conventions.md

/**
 * Derives list state from a ts-rest `useQuery` result for a paginated list
 * endpoint. A failed read must show as an error, not an empty list. The
 * ts-rest client throws non-2xx, so a failure arrives as `isError` — including
 * on a *background refetch* that failed after an earlier 200, since TanStack Query keeps serving the last-known-good `data` in that
 * case. `isError` is therefore checked first, unconditionally: stale 200 data
 * never masks a current fetch failure.
 */
export interface ListQueryState<T> {
  items: T[];
  nextCursor: string | null;
  hasError: boolean;
}

export function deriveListQuery<T>(query: {
  data?: { status: number; body: unknown } | undefined;
  isError: boolean;
}): ListQueryState<T> {
  const { data, isError } = query;
  if (!isError && data?.status === 200) {
    const body = data.body as { items: T[]; next_cursor: string | null };
    return { items: body.items, nextCursor: body.next_cursor, hasError: false };
  }
  return { items: [], nextCursor: null, hasError: isError || data !== undefined };
}
